/* --external-- */
import _ from "lodash";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

/* --relative-- */
import classes from "./MatchingView.scss";
import {
  getAttemptedAnswerValue,
  getFilteredResponses,
  getSelectedValues,
  isAttempted,
  isCorrect,
} from "../MatchingFieldUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

/* --internal-- */
import {
  Button,
  ButtonDisclosure,
  CardSelection,
  Dropdown,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  CloseCircleFilled,
  DeleteOutlined,
  EyeHiddenOutlined,
  EyeOutlined,
  PencilLineOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import MiniDocEditor from "UIComponents/DocEditor/MiniDocEditorWrapper";

/* --constants-- */
import { colors } from "Constants";
import {
  DOC_EDITOR_STYLES,
  MODE_ASSESSMENT_VIEW,
  MODE_LIBRARY_VIEW,
  MODE_RESTRICTED_ASSESSMENT_VIEW,
  MODE_STUDENT_ATTEMPT,
  MODE_TEACHER_PREVIEW,
} from "Worksheet/modules/WorksheetConstants";

/* --styles-- */
const styles = {
  dropdownStyles: {
    padding: "8px",
    gap: "8px",
  },
  correctBackground: {
    backgroundColor: colors.surfaceSuccess,
  },
  wrongBackground: {
    backgroundColor: colors.surfaceError,
  },
};

// * Component for matching field in view mode
const MatchingDocEditorView = props => {
  const { itemValue } = props;
  return (
    <MiniDocEditor
      value={itemValue}
      viewOnly={true}
      width={"100%"}
      containerStyle={DOC_EDITOR_STYLES.MINI_EDITOR_CONTAINER_STYLES}
      styles={DOC_EDITOR_STYLES.MINI_EDITOR_MATCHING_STYLES_VIEW}
      editorClassName={classes.miniEditorContainer}
    />
  );
};

/*
  @params { answersOptions, matchingPairsResponse, item, chooseAnswer }
  @returns Dropdown to display the answer options
 */
const EditAnswerDropdown = props => {
  const {
    answersOptions,
    matchingPairsResponse,
    item,
    chooseAnswer,
    showDocEditor,
  } = props;
  const [overlayVisible, setOverlayVisible] = useState(false);

  return (
    <Dropdown
      overlay={
        <div className={classes.dropdownMenu}>
          {_.map(answersOptions, ansItem => (
            <div
              className={classes.dropdownMenuItem}
              key={_.get(ansItem, "key")}
            >
              <CardSelection
                title={
                  showDocEditor ? (
                    <MatchingDocEditorView
                      itemValue={_.get(ansItem, "label")}
                    />
                  ) : (
                    _.get(ansItem, "label")
                  )
                }
                type="radio"
                key={_.get(ansItem, "key")}
                value={_.get(ansItem, "label")}
                style={styles.dropdownStyles}
                selectedValues={getSelectedValues(
                  matchingPairsResponse,
                  answersOptions,
                  item
                )}
                onCardSelectionClick={() =>
                  chooseAnswer(item, ansItem, setOverlayVisible)
                }
                testId="MatchingField-MatchingView-dropdownMenuOption"
              />
            </div>
          ))}
        </div>
      }
      visible={overlayVisible}
    >
      <IconButton
        dsVersion="2.0"
        type="plain"
        variant="neutral"
        size="medium"
        _legacy_variant="plain"
        _legacy_size="medium"
        icon={<PencilLineOutlined />}
        onClick={() => setOverlayVisible(!overlayVisible)}
        testId="MatchingField-MatchingView-openDropdownMenu"
      />
    </Dropdown>
  );
};

/*
  @params { t, matchingPairs, mode, updateInputField, matchingResponseFromField, showViewSolutionButton } 
  @returns Matching Question Type Field
 */
const MatchingView = props => {
  const {
    t,
    mode,
    matchingPairs,
    matchingResponseFromField,
    updateInputField,
    showViewSolutionButton,
    isSubmitted,
  } = props;

  const showDocEditor = getIsWorksheetDocEditorEnabled();

  const [previewModeField, setPreviewModeField] = useState(
    _.get(matchingResponseFromField, "value", []) || []
  );

  const isPreviewMode = _.includes(
    [MODE_TEACHER_PREVIEW, MODE_RESTRICTED_ASSESSMENT_VIEW],
    mode
  );
  const matchingResponseField = isPreviewMode
    ? previewModeField
    : _.get(matchingResponseFromField, "value", []) || [];

  const [answersOptions, setAnswerOptions] = useState(
    _.uniqBy(
      _.map(matchingPairs, item => ({
        key: _.get(item, "answer.id"),
        label: _.get(item, "answer.value"),
      })),
      "key"
    )
  );
  const [showAnswer, setShowAnswer] = useState(false);
  const isAssessmentMode = _.includes(
    [MODE_ASSESSMENT_VIEW, MODE_LIBRARY_VIEW],
    mode
  );

  const isAttemptMode = _.includes(
    [
      MODE_STUDENT_ATTEMPT,
      MODE_TEACHER_PREVIEW,
      MODE_RESTRICTED_ASSESSMENT_VIEW,
    ],
    mode
  );

  const isDropdownDisabled = _.isEqual(mode, MODE_RESTRICTED_ASSESSMENT_VIEW);

  const onUpdateMatchingPairResponse = updatedMatchingPairsResponse => {
    if (mode === MODE_STUDENT_ATTEMPT) {
      const filteredMatchingPairsResponse = getFilteredResponses(
        updatedMatchingPairsResponse,
        matchingPairs
      );
      const params = { value: filteredMatchingPairsResponse };
      updateInputField({
        params,
        isDebounce: true,
        isMutation: true,
      });
    } else if (isPreviewMode) {
      setPreviewModeField(updatedMatchingPairsResponse);
    }
  };

  //  Reordering the answer options
  useEffect(() => {
    if (isAttemptMode) {
      setAnswerOptions(prev => _.shuffle(prev));
    }
  }, []);

  //  Adding the student choice for a prompt
  const chooseAnswer = (item, ansItem, setOverlayVisible) => {
    if (_.isFunction(setOverlayVisible)) {
      setOverlayVisible(prev => !prev);
    }

    const promptId = _.get(item, "prompt.id", null);
    if (_.isNil(promptId)) {
      return;
    }

    const promptAnswer = {
      promptId: promptId,
      answerId: _.get(ansItem, "key", null),
    };

    const updatedMatchingPairsAnswers = _.cloneDeep(matchingResponseField);
    const idx = _.findIndex(matchingResponseField, {
      promptId: promptId,
    });

    if (idx !== -1) {
      updatedMatchingPairsAnswers[idx] = promptAnswer;
    } else {
      updatedMatchingPairsAnswers.push(promptAnswer);
    }
    onUpdateMatchingPairResponse(updatedMatchingPairsAnswers);
  };

  //  Deleting the choice of student
  const deleteStudentChoice = value => {
    const promptId = _.get(value, "prompt.id");
    const updatedMatchingPairsAnswers = _.cloneDeep(matchingResponseField);
    _.remove(updatedMatchingPairsAnswers, { promptId: promptId });
    onUpdateMatchingPairResponse(updatedMatchingPairsAnswers);
  };

  const cellWrapperClasses = classNames("text-body-1", "w-full", "min-w-0");

  return (
    <div>
      {/* Viewing Solution tag */}
      {showAnswer && (
        <div className={classes.buttonContainer}>
          <div className={classes.tag}>{t("worksheet:viewing_solution")}</div>
        </div>
      )}

      {_.map(
        matchingPairs,
        item =>
          !_.isEmpty(_.get(item, "prompt.value")) && (
            <div
              key={`${_.get(item, "prompt.id", "prompt")}-${_.get(
                item,
                "answer.id",
                "answer"
              )}`}
              className={classes.rowContainer}
              style={
                showViewSolutionButton && !showAnswer && isSubmitted
                  ? isCorrect(item, matchingResponseField)
                    ? styles.correctBackground
                    : styles.wrongBackground
                  : null
              }
            >
              {/* Prompt */}
              <div className={classes.cellContainer}>
                <div className={cellWrapperClasses}>
                  {showDocEditor ? (
                    <MatchingDocEditorView
                      itemValue={_.get(item, "prompt.value", "")}
                    />
                  ) : (
                    _.get(item, "prompt.value", "")
                  )}
                </div>
              </div>

              {/* Answer */}
              <div className={classes.cellContainerRight}>
                <div className={classes.textContainer}>
                  {isAssessmentMode ||
                  (showViewSolutionButton && showAnswer) ? (
                    // Correct Answer Value
                    <div className={cellWrapperClasses}>
                      {showDocEditor ? (
                        <MatchingDocEditorView
                          itemValue={_.get(item, "answer.value", "")}
                        />
                      ) : (
                        _.get(item, "answer.value", "")
                      )}
                    </div>
                  ) : // Attempted Answer Value
                  isAttempted(item, matchingResponseField) ? (
                    <div className={cellWrapperClasses}>
                      {showDocEditor ? (
                        <MatchingDocEditorView
                          itemValue={_.get(item, "answer.value", "")}
                        />
                      ) : (
                        getAttemptedAnswerValue(
                          answersOptions,
                          matchingResponseField,
                          item
                        )
                      )}
                    </div>
                  ) : // Dropdown if not attempted
                  isAttemptMode ? (
                    <div className={classes.positionRelative}>
                      <Dropdown
                        overlay={
                          <div className={classes.dropdownMenu}>
                            {_.map(answersOptions, ansItem => (
                              <div
                                className={classes.dropdownMenuItem}
                                key={_.get(ansItem, "key")}
                              >
                                <CardSelection
                                  title={
                                    showDocEditor ? (
                                      <MatchingDocEditorView
                                        itemValue={_.get(ansItem, "label")}
                                      />
                                    ) : (
                                      _.get(ansItem, "label")
                                    )
                                  }
                                  type="radio"
                                  key={_.get(ansItem, "key")}
                                  value={_.get(ansItem, "label")}
                                  style={styles.dropdownStyles}
                                  onCardSelectionClick={() =>
                                    chooseAnswer(item, ansItem)
                                  }
                                  testId="MatchingField-MatchingView-selectDropdownOption"
                                />
                              </div>
                            ))}
                          </div>
                        }
                        disabled={isDropdownDisabled}
                        testId="MatchingField-MatchingView-dropdownMenu"
                      >
                        <ButtonDisclosure
                          dsVersion="2.0"
                          variant="neutral"
                          type="outlined"
                          size="medium"
                          _legacy_variant="outlined-subtle"
                          _legacy_size="medium"
                          testId="MatchingField-MatchingView-openDropdownMenu"
                        >
                          <span className="whitespace-nowrap">
                            {t(
                              window.outerWidth <= 776
                                ? "common:select"
                                : "worksheet:select_answer"
                            )}
                          </span>
                        </ButtonDisclosure>
                      </Dropdown>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>

                {showViewSolutionButton && !showAnswer && isSubmitted && (
                  <div className={classes.iconIndicator}>
                    {isCorrect(item, matchingResponseField) ? (
                      <TickCircleFilled variant="success" />
                    ) : (
                      <CloseCircleFilled variant="critical" />
                    )}
                  </div>
                )}

                {/* Answer Options */}
                {isAttemptMode && isAttempted(item, matchingResponseField) && (
                  <div className={classes.iconContainer}>
                    <EditAnswerDropdown
                      answersOptions={answersOptions}
                      matchingPairsResponse={_.get(
                        matchingResponseFromField,
                        "value",
                        []
                      )}
                      item={item}
                      chooseAnswer={chooseAnswer}
                      showDocEditor={showDocEditor}
                    />

                    <IconButton
                      dsVersion="2.0"
                      type="plain"
                      variant="neutral"
                      size="medium"
                      _legacy_variant="plain"
                      _legacy_size="medium"
                      onClick={() => deleteStudentChoice(item)}
                      icon={<DeleteOutlined />}
                      testId="MatchingField-MatchingView-deleteStudentChoice"
                    />
                  </div>
                )}
              </div>
            </div>
          )
      )}

      {/* View solution toggle */}
      {showViewSolutionButton && (
        <div className={classes.buttonContainer}>
          <Button
            size="medium"
            variant="neutral"
            dsVersion="2.0"
            type="outlined"
            _legacy_size="small"
            _legacy_variant="outlined"
            icon={showAnswer ? <EyeHiddenOutlined /> : <EyeOutlined />}
            onClick={() => {
              setShowAnswer(prev => !prev);
            }}
            testId="MatchingField-MatchingView-showAnswer"
          >
            {showAnswer
              ? t("worksheet:hide_solution")
              : t("worksheet:view_solution")}
          </Button>
        </div>
      )}
    </div>
  );
};

MatchingView.propTypes = {
  t: PropTypes.func.isRequired,
  mode: PropTypes.string,
  updateInputField: PropTypes.func,
  matchingResponseFromField: PropTypes.object,
  matchingPairs: PropTypes.array,
  showViewSolutionButton: PropTypes.bool,
  isSubmitted: PropTypes.bool,
};

MatchingView.defaultProps = {
  mode: "view",
  matchingResponseFromField: {},
  matchingPairs: [],
  showViewSolutionButton: false,
  isSubmitted: false,
};

export default MatchingView;
